import { useState, useCallback, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { api, HTTP_UNAUTHORIZED } from "./lib/api";
import { useTheme } from "./hooks/useTheme";

import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";

// Lazy load heavy pages
const LinksPage = lazy(() => import("./pages/LinksPage"));
const OffersPage = lazy(() => import("./pages/OffersPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const FiltersPage = lazy(() => import("./pages/FiltersPage"));
const QuickAddPage = lazy(() => import("./pages/QuickAddPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-40">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const DATA_REFRESH_INTERVAL_MS = 60 * 1000; // 60s instead of 30s

function AppContent() {
  const { isDark, toggleTheme } = useTheme();
  const { isAuthenticated, isCheckingAuth, handleLogout, handleAuthError, handleAuthSuccess } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [stats, setStats] = useState({
    total_links: 0, total_offers: 0, new_offers: 0,
    added_to_database: 0, offers_today: 0,
  });
  const [links, setLinks] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({ sms_template: "", telegram_chat_id: "", max_offer_age_hours: 24 });

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem("unity_token");
    if (!token) return;
    try {
      // Only fetch stats and links on interval — messages and settings are rarely needed
      const [statsRes, linksRes] = await Promise.all([
        api.getStats(), api.getScanLinks(),
      ]);
      if (localStorage.getItem("unity_token")) {
        setStats(statsRes.data);
        setLinks(linksRes.data);
        handleAuthSuccess();
      }
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        handleAuthError();
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, handleAuthError, handleAuthSuccess]);

  // Fetch messages and settings only once on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchExtras = async () => {
      try {
        const [messagesRes, settingsRes] = await Promise.all([
          api.getMessages(), api.getSettings(),
        ]);
        setMessages(messagesRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.warn("Nie udało się pobrać wiadomości/ustawień:", err?.message);
      }
    };
    fetchExtras();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      const interval = setInterval(fetchData, DATA_REFRESH_INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchData]);

  const handleScan = async () => {
    setIsScanning(true);
    try {
      await api.triggerScan();
      toast.success("Skanowanie rozpoczete");
      setTimeout(fetchData, 5000);
    } catch {
      toast.error("Blad");
    } finally {
      setIsScanning(false);
    }
  };

  const handleSaveSettings = async (newSettings) => {
    await api.updateSettings(newSettings);
    setSettings(newSettings);
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen bg-background overflow-hidden">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} onLogout={handleLogout} />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <Header
            toggleTheme={toggleTheme}
            isDark={isDark}
            setIsOpen={setSidebarOpen}
            onScan={handleScan}
            isScanning={isScanning}
          />

          <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<DashboardPage stats={stats} isLoading={isLoading} />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/links" element={<LinksPage links={links} onRefresh={fetchData} />} />
                <Route path="/offers" element={<OffersPage onRefresh={fetchData} />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/filters" element={<FiltersPage onRefresh={fetchData} />} />
                <Route path="/quick-add" element={<QuickAddPage />} />
                <Route path="/messages" element={<MessagesPage messages={messages} />} />
                <Route path="/settings" element={<SettingsPage settings={settings} onSave={handleSaveSettings} />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <AppContent />
    </AuthProvider>
  );
}

export default App;
